import { getJobList, getJobForm, saveLinkForm, saveJobForm } from "../../api"
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query"
import { normalizeAddressBondNo, wasBondNoTruncated } from "../Form/addressFieldConfig"

// TODO
// get job lists for the current user's company (open, pending, submitted, archived)
/*
export const useJobList = ({ listType }, config) => {
    const listInURL = useRouter().query?.list

    if (listInURL && !listType) {
        listType = parseInt(listInURL)
    }

	// TODO: Confirm this works
	if ( !['open', 'pending', 'submitted', 'archived'].includes(listType) ) {
		
		// TODO: Confirm how throwing an error should be done
		return {
			error: {
				code: 401,
				message: "Invalid list type" +listType;
			}
		}
		
	}

    return useQuery(
		['jobList', { type: listType }],
        () => getJobList(listType),
        mergeConfigs(
			{
				// enabled: !!projectId,
            },
            config
        )
    )
}
*/
export const jobKey = (params) => {

	// Find the first defined value in order of priority: id -> uuid -> appid
    let keyValue = params.id;
	if ( params.uuid ) {
		keyValue = params.uuid;
	} else if ( params.appid ) {
		keyValue = params.appid;
	}
    
    if (!keyValue) {
        console.warn('No valid key found in params:', params);
    }
    
    const key = ['form', String(keyValue)];
    return key;
}

// get the form/job data/results/etc for a specific job using the job id/uuid/share uuid/etc
export const useJobForm = (params, accessToken) => {
	return useQuery({
		queryKey: jobKey(params),
		queryFn: async () => {
			try {
				const data = await getJobForm(params, accessToken);
				const massagedData = massageData(data);
				return massagedData;
			} catch (error) {
				throw error;
			}
		},
		enabled: !!(params /* && accessToken */)
	})
}



// get the form/job data/results/etc for a specific job using the job id/uuid/share uuid/etc
export const useUpdateJobFormLink = ({ jobId, uuid }, config) => {
	
	// only one of these will be defined
	// a "new" job link (that contains an appid) cannot save incremental changes
    const queryClient = useQueryClient()

    return useMutation(
        async (data) => {
			const response = await saveLinkForm({ id: jobId, answers: data, uuid });
			if (response.success === false) {
				throw new Error(response.error || 'An error occurred while saving the form');
			}
			return response;
		},
		{
			enabled: !!jobId && !isNaN(jobId) && !!uuid,
			onSuccess: (data) => {
				queryClient.setQueryData(jobKey({ id: jobId, uuid }), (existingData) => {
					// Merge new result values into existing answerData
					const updatedAnswerData = { ...existingData.answerData };
					data.results.forEach(result => {
						const existing = updatedAnswerData[result.field_id];
						if (existing?.address !== undefined) {
							// Address field - parse JSON value and merge
							const parsed = result.value ? JSON.parse(result.value) : {};
							updatedAnswerData[result.field_id] = {
								...existing,
								address: normalizeAddressBondNo({ ...existing.address, ...parsed })
							};
						} else if (existing) {
							// Other field types - update value (parse JSON arrays for multi-select)
							let value = result.value;
							if (value && value.startsWith('[')) {
								try { value = JSON.parse(value); } catch (e) {}
							}
							updatedAnswerData[result.field_id] = {
								...existing,
								value
							};
						}
					});
					queryClient.invalidateQueries({ queryKey: ['jobs', 'open'] }); // linked job updates should only be submitted on "open" jobs

					return {
						...existingData,
						results: data.results,
						answerData: updatedAnswerData,
						progress: data.progress
					};
				});
			}
			// onSettled: () => {
			// 	queryClient.invalidateQueries({ queryKey: ['jobs', data.status] });
			// }
		}
    )
}

// get the form/job data/results/etc for a specific job using the job id/uuid/share uuid/etc
export const useUpdateJobForm = ({ jobId }, config) => {
    // const jobIdInUrl = useRouter().query?.job_id		// TODO: Find out if I can do this.

    // if (jobIdInUrl && !jobId) {
    //     jobId = parseInt(jobId)
    // }

    const queryClient = useQueryClient()

    return useMutation(
        async (data) => {
			const response = await saveJobForm({ id: jobId, answers: data });
			if (response.success === false) {
				throw new Error(response.error || 'An error occurred while saving the form');
			}
			return response;
		},
		{
			enabled: !!jobId && !isNaN(jobId),
			onSuccess: (data) => {
				queryClient.setQueryData(jobKey({ id: jobId }), (existingData) => {
					// Merge new result values into existing answerData
					const updatedAnswerData = { ...existingData.answerData };
					data.results.forEach(result => {
						const existing = updatedAnswerData[result.field_id];
						if (existing?.address !== undefined) {
							// Address field - parse JSON value and merge
							const parsed = result.value ? JSON.parse(result.value) : {};
							updatedAnswerData[result.field_id] = {
								...existing,
								address: normalizeAddressBondNo({ ...existing.address, ...parsed })
							};
						} else if (existing) {
							// Other field types - update value (parse JSON arrays for multi-select)
							let value = result.value;
							if (value && value.startsWith('[')) {
								try { value = JSON.parse(value); } catch (e) {}
							}
							updatedAnswerData[result.field_id] = {
								...existing,
								value
							};
						}
					});
					queryClient.invalidateQueries({ queryKey: ['jobs', data.status] }); // job updates only invalidate the specific status list that the job is on.

					return {
						...existingData,
						results: data.results,
						answerData: updatedAnswerData,
						progress: data.progress
					};
				});
			}
		}
    )
}

// process of an array of [ field_id: { field_id: value} ] pairs. value is setup based off field type (address, choice/radio, text, etc)
const processFieldAnswers = (field, result) => {

	if ( field.type === 'address' ) {
				
		const addr_results = result === null ? '' : JSON.parse(result.value)
		let address_block = {
			company: '',
			name: '',
			phone: '',
			address: '',
			address_2: '',
			parcel_no: '',
			lot_no: '',
			block_no: '',
			county: '',
			id: '',
			city: '',
			state: '',
			zip: '',
			email: ''
		};
		// initialize contact address block for bonding companies with blank bond_no field
		if ( field.type == 10 || field.type == 11 || field.type == 12 ) {
			address_block.bond_no = '';
		}

		const mergedAddress = {
			...address_block,
			...addr_results,
		};

		return {
			id: field.id,
			address: normalizeAddressBondNo(mergedAddress),
			bondNoTruncated: wasBondNoTruncated(mergedAddress.bond_no),
		}

	} else {

		let output;
		if (result?.value) {
			
			if (field.type === 'multi-select' ) {
				output = JSON.parse(result.value);
			} else {
				output = result.value;
			}

		} else if (field.defaultValue !== '') {
			output = field.defaultValue;
		} else {
			output = null;
		}

		if (field.type === 'choice' && output == '') {
			return {
				id: field.id,
				value: '',
			}
		}

		return {
			id: field.id, 
			value: output
		}
		// if ( field.type === "assoc_choice" ) {
		// 	if ( newAnswerData[field.id]?.value ) {
		// 		// loop through field choices
		// 		field.choices.forEach(choice => {
		// 			if ( choice.id == answerData[field.id]?.value ) {
		// 				chosen_value = choice.value;
		// 				additional_party = choice.extra;
		// 			}
		// 		})
		// 	}
		// }
	}
}

// configure the response from when a form is initially loaded
const massageData = (data) => {

	// Add error handling for unexpected data structure
	if (!data || !data.form || !data.form.sections) {
		console.error('massageData: Invalid data structure received:', data);
		return data || {};
	}

	var chosen_value = 0;
	var additional_party = 0;

	let limit = 0;
	let answerData = {}

	data.form.sections.forEach(section => {
		section.fields.forEach(field => {

			var result = field?.result;
			answerData[field.id] = processFieldAnswers(field, result);

			// process choices for "customer role" field
			if ( field.type === "assoc_choice" ) {
				
				// value set, meaning the chosen value can be evaluated to determine section visibility, etc.
				if ( answerData[field.id]?.value ) {
					// loop through field choices
					field.choices.forEach(choice => {
						if ( choice.id == answerData[field.id]?.value ) {
							chosen_value = choice.value;
							additional_party = choice.extra;
						}
					})
				}

			}
		})
	})

	// Set each section to "visible", unless an Association Option has been chosen, then show/hide sections accordingly
	/*
	data.form.sections.map(section => {
		if ( chosen_value !== 0 ) {
			var address_found = false
			
			for( var i=0; i<section.fields.length; i++ ) {
				let field = section.fields[i];
				if ( field['type'] == 'address' ) {
					if ( !field.mapped_type || field?.mapped_type?.reference == 'project_location' ) continue;
					// if ( field.type_id == 2 || field.type_id == null ) continue;
					if ( field.type_id > 2 && field.type_id <= 8 ) {
						if (additional_party == 1) {
							section['visible'] = (chosen_value >= field.type_id) ? true : false
						} else {
							section['visible'] = (chosen_value > field.type_id) ? true : false
						}
						address_found = true
					} else {
						// Mark sections that are above 5 in contractual chain to always display
						section['visible'] = true
						address_found = true
					}
				}
			}
			if ( !address_found ) section['visible'] = true
		
		} else {
			section['visible'] = true
		}
	})
	*/

	return {
		...data,
		answerData
	}
}

// TODO: use this in anyway?
// from portal
/*
export const useProject = ({ projectId, takeover = false }, config) => {
    const projectIdInUrl = useRouter().query?.project_id

    if (projectIdInUrl && !projectId) {
        projectId = parseInt(projectIdInUrl)
    }

    return useQuery(
        Project.keys.project(projectId),
        () => Project.get(projectId, takeover),
        mergeConfigs(
            {
                enabled: !!projectId,
            },
            config
        )
    )
}
*/
